import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";
import SnGrid from "@shared/components/GridV2/SnGrid";
import CommonSpinner from "@widgets/CommonSpinner";

interface popupProps {
  Grid1SelectedData?: any;
  footerdata?: any;
  clonedata?: any[];
}

const SN_TRA_IBL_REG_004_POP_01 = forwardRef(
  (
    { Grid1SelectedData, footerdata, clonedata = [] }: popupProps,
    ref: React.Ref<any>
  ) => {
    const { userInfo } = global();

    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });

    const [columns, setColums] = useState<any>({ grid1: [], grid2: [] });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });

    const [isLoading, setIsLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => ({ flag: await onOk() }),
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_TRA_IBL_REG_004_POP_01",
            {}
          );
          setColums({ grid1: rtn.grid.grid1, grid2: rtn.grid.grid2 });
          await onSearch();
        } catch (error: any) {
          showDialog("alter", error);
        }
      };
      fetchform();
    }, []);

    const getGrid1Rows = (): any[] => {
      const grid1 = gridRefs.current["grid1"].current;
      const rows: any[] = [];
      for (let i = 0; i < grid1.getDisplayedRowCount(); i++) {
        rows.push(grid1.getRowNodeAtIndex(i).data);
      }
      return rows;
    };

    const getGrid2Rows = (): any[] => {
      const rows: any[] = [];
      gridRefs.current["grid2"].current.forEachNode((node: any) => {
        rows.push({ ...node.data });
      });
      return rows;
    };

    // 확인: 부대비용별 배부합계 = 잔액 검증 → 부가세율 조회 → 저장. 성공 시 true.
    const onOk = async (): Promise<boolean> => {
      try {
        setIsLoading(true);
        const expenses = getGrid1Rows();
        const grid2Rows = getGrid2Rows();

        for (let k = 0; k < expenses.length; k++) {
          const rowH = expenses[k];
          const distSum = grid2Rows
            .filter(
              (r) =>
                r.no_req === rowH.no_expense &&
                r.no_line_req === rowH.no_line_expense
            )
            .reduce((sum, r) => sum + Number(r.am_dist), 0);
          if (Number(rowH.am_re) !== distSum) {
            await showDialog(
              "alter",
              "부대비용금액과 배부금액 합계가 맞지 않습니다."
            );
            gridRefs.current["grid1"].current.selectAndFocusRow(k);
            return false;
          }
        }

        const vat = await callApi("SN_TRA_IBL_REG_004_VAT", {
          cd_firm: userInfo.cd_firm,
          no_po: clonedata[0].no_po,
        });
        if (!vat.status) return false;
        const { fg_vat = "", cd_fin = "", rt_vat = 10 } = vat.data?.[0] ?? {};

        return await onSave(
          cd_fin,
          fg_vat,
          Number(rt_vat),
          expenses,
          grid2Rows
        );
      } catch (error: any) {
        await showDialog("error", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    };

    const onSave = async (
      cd_fin: string,
      fg_vat: string,
      rt_vat: number,
      expenses: any[],
      grid2Rows: any[]
    ): Promise<boolean> => {
      const no_pur = clonedata[0].no_io;
      const sumDistByItem = (cd_item: string) =>
        grid2Rows
          .filter((r) => r.cd_item == cd_item)
          .reduce((sum, r) => sum + Number(r.am_dist), 0);

      const purchaseH: any = {
        cd_firm: userInfo.cd_firm,
        no_pur: no_pur,
        dt_pur: Grid1SelectedData.dt_pur,
        cd_cust: Grid1SelectedData.cd_cust,
        cd_fin: cd_fin,
        fg_trns: Grid1SelectedData.fg_trns,
        cd_exch: Grid1SelectedData.cd_exch,
        rt_exch: Grid1SelectedData.rt_exch,
        cd_gp: Grid1SelectedData.cd_gp,
        cd_emp: Grid1SelectedData.cd_emp,
        am: footerdata.am_footer,
        am_k: footerdata.am_k_footer,
        am_vat: 0,
        am_supply: footerdata.am_k_footer,
        cd_mgmtdtl: "",
        dt_plan_pay: Grid1SelectedData.dt_pur,
        fg_pay: "",
        dc_rmk: "",
        cd_biz: Grid1SelectedData.cd_biz,
        fg_vat: fg_vat,
        cd_dept: Grid1SelectedData.cd_dept,
        yn_autoprocess: "Y",
      };

      const baseLine = (childrow: any) => ({
        cd_firm: userInfo.cd_firm,
        no_pur: no_pur,
        no_line: childrow.no_line,
        cd_item: childrow.cd_item,
        qt: childrow.qt,
        qt_io_spec01: childrow.qt_io_spec01,
        qt_io_spec02: childrow.qt_io_spec02,
        no_rcv: childrow.no_rcv,
        no_line_rcv: childrow.no_line_rcv,
        no_po: childrow.no_po,
        no_line_po: childrow.no_line_po,
        cd_pjt: childrow.cd_pjt,
        no_wbs: childrow.no_wbs,
        no_cbs: childrow.no_cbs,
        dc_rmk: childrow.dc_rmk,
        fg_io: childrow.fg_io,
        am_dist_k: sumDistByItem(childrow.cd_item),
      });

      // 부대비용이 없으면 수입품 금액을 그대로 매입으로 넘긴다
      if (expenses.length === 0) {
        const purchaseL = clonedata.map((childrow: any) => ({
          ...baseLine(childrow),
          um: childrow.um,
          am: childrow.am,
          am_k: childrow.am_k,
          am_vat: childrow.am_vat,
          um_supply: childrow.um_supply,
          am_supply: childrow.am_supply,
        }));
        const ok = await fnSave("SN_TRA_IBL_REG_004_SAVE_01", {
          pur_purchaseh: { insert: [purchaseH] },
          pur_purchasel: { insert: purchaseL },
        });
        return !!ok;
      }

      const purchaseL = clonedata.map((childrow: any) => {
        const qt = Number(childrow.qt ?? 0);
        const am_k = Number(childrow.am_k ?? 0);
        const am_vat = Math.trunc((am_k * rt_vat) / 100);
        return {
          ...baseLine(childrow),
          um: am_k / qt / Grid1SelectedData.rt_exch,
          am: childrow.am,
          am_vat: am_vat,
          am_k: am_k + am_vat,
          um_supply: (am_k + am_vat) / qt,
          am_supply: am_k + am_vat,
          um_k: am_k / qt,
        };
      });

      const sum = (key: string) =>
        purchaseL.reduce((s: number, r: any) => s + Number(r[key] ?? 0), 0);
      purchaseH.am = sum("am");
      purchaseH.am_k = sum("am_k");
      purchaseH.am_vat = sum("am_vat");
      purchaseH.am_supply = sum("am_supply");

      // 부대비용 라인별 배부(tra_expensel_purchase). tra_expensel.am_dist(정산금액)를 갱신한다.
      const expensePurchase = expenses.map((rowH: any, i: number) => ({
        cd_firm: userInfo.cd_firm,
        no_pur: no_pur,
        no_line: i + 1,
        no_expense: rowH.no_expense,
        no_line_expense: rowH.no_line_expense,
        cd_expense: rowH.cd_expense,
        am_dist: rowH.am_expense,
      }));

      // 부대비용 × 수입품 배부 상세(tra_expensel_purchase_item). PK: no_pur + no_line(부대비용 순번) + no_line_pur(품목 항번)
      const expensePurchaseItem = grid2Rows.map((r: any) => ({
        cd_firm: userInfo.cd_firm,
        no_pur: no_pur,
        no_line: r.no_line_tra,
        no_line_pur: r.no_line,
        cd_item: r.cd_item,
        no_expense: r.no_expense,
        no_line_expense: r.no_line_req,
        cd_expense: r.cd_expense,
        am_dist: r.am_dist,
      }));

      const ok = await fnSave("SN_TRA_IBL_REG_004_SAVE_01", {
        pur_purchaseh: { insert: [purchaseH] },
        pur_purchasel: { insert: purchaseL },
        tra_expensel_purchase: { insert: expensePurchase },
        tra_expensel_purchase_item: { insert: expensePurchaseItem },
      });
      return !!ok;
    };

    // 통관(no_ip) 라인별 관세
    const getTariffByLine = async (no_ip: any) => {
      const res = await callApi("SN_TRA_IBL_REG_004_TARIFF", {
        cd_firm: userInfo.cd_firm,
        no_ip,
      });
      const byLine: Record<string, number> = {};
      for (const r of res.data ?? [])
        byLine[String(r.no_line)] = Number(r.am_tariff ?? 0);
      return byLine;
    };

    // 부대비용(grid1) × 수입품(clonedata)으로 배부행(grid2)을 생성. am_k 비율 배부, 관세(900)는 tra_ipl 금액 사용.
    const onSearch = async () => {
      try {
        const data: any[] = await fnGetDataSet({
          id: "sn_tra_ibl_reg_004_pop_01",
          params: {
            cd_firm: userInfo.cd_firm,
            no_req: Grid1SelectedData.no_req,
            no_ip: Grid1SelectedData.no_ip,
          },
        });
        const expenses = data[0] ?? [];
        const sumAmk = Number(footerdata?.am_k_footer ?? 0);

        const childRows: any[] = [];
        for (let i = 0; i < expenses.length; i++) {
          const masterRow = expenses[i];
          const am_expense = Number(masterRow.am_expense ?? 0);
          const isTariff = masterRow.cd_expense === "900" && masterRow.no_ip; // 관세(TRA008)
          const tariffByLine = isTariff
            ? await getTariffByLine(masterRow.no_ip)
            : {};

          for (const baseRow of clonedata) {
            const qt = Number(baseRow.qt ?? 0);
            const am_k = Number(baseRow.am_k ?? 0);
            let am_dist: number;
            let am: number;
            if (isTariff) {
              am_dist = tariffByLine[String(baseRow.no_line)] ?? 0;
              am = am_dist + am_k;
            } else {
              am_dist = Math.floor(
                sumAmk !== 0 ? (am_k / sumAmk) * am_expense : 0
              );
              am = am_dist + Math.floor(am_k);
            }

            // baseRow 스프레드 금지: KeyField가 복제되어 그리드 행 키가 충돌한다.
            childRows.push({
              cd_item: baseRow.cd_item,
              nm_item: baseRow.nm_item,
              dc_item_spec: baseRow.dc_item_spec,
              dc_item_unit: baseRow.dc_item_unit,
              qt: qt,
              um_k: baseRow.um_k,
              am_k: am_k,
              am_dist: am_dist,
              am: am,
              um: qt !== 0 ? Math.floor(am) / qt : 0,
              no_io: baseRow.no_io,
              no_req: masterRow.no_expense,
              no_line_req: masterRow.no_line_expense,
              no_pur: baseRow.no_pur,
              no_rcv: baseRow.no_rcv,
              no_line_rcv: baseRow.no_line_rcv,
              no_po: baseRow.no_po,
              no_line_po: baseRow.no_line_po,
              cd_pjt: baseRow.cd_pjt,
              no_wbs: baseRow.no_wbs,
              no_cbs: baseRow.no_cbs,
              dc_rmk: baseRow.dc_rmk,
              fg_io: baseRow.fg_io,
              no_line_pur: baseRow.no_line_pur,
              no_expense: masterRow.no_expense,
              no_line_expense: masterRow.no_line_expense,
              cd_expense: masterRow.cd_expense,
              no_line: baseRow.no_line,
              no_line_tra: i + 1,
              yn_manual: masterRow.yn_manual === "Y" ? "Y" : "N",
            });
          }
        }

        // 배부 절사 차액은 마지막 배부행에 보정
        for (const parentRow of expenses) {
          const children = childRows.filter(
            (c) =>
              c.no_req === parentRow.no_expense &&
              c.no_line_req === parentRow.no_line_expense
          );
          if (children.length === 0) continue;
          const diff =
            Number(parentRow.am_expense ?? 0) -
            children.reduce((sum, c) => sum + Number(c.am_dist ?? 0), 0);
          if (diff <= 0) continue;
          const last = children[children.length - 1];
          last.am_dist += diff;
          last.am = last.am_dist + Number(last.am_k ?? 0);
          last.um = last.qt !== 0 ? last.am / last.qt : 0;
        }

        setDataSources({ grid1: expenses, grid2: childRows });
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    const onFocusedRowChanged1 = (row: any) => {
      if (!row) return;
      gridRefs.current["grid2"].current.applyFilters({
        no_req: row.no_expense,
        no_line_req: row.no_line_expense,
      });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
    };

    const onCellValueChanged1 = (event: CellValueChangedEvent) => {
      if (!event) return;

      const colId = event.colId;
      const rowData = event.rowData;

      if (colId === "yn_manual") {
        gridRefs.current["grid2"].current.forEachNode((node: RowNode) => {
          if (
            node.data.no_req !== rowData.no_expense ||
            node.data.no_line_req !== rowData.no_line_expense
          )
            return;
          node.setDataValue("yn_manual", rowData.yn_manual, "noEvent");
        });
      }
    };

    // 배부금액 수동 수정 시 정산금액/단위원가 재계산
    const onCellValueChanged2 = (event: CellValueChangedEvent) => {
      if (!event) return;

      const colId = event.colId;
      const rowData = event.rowData;
      const rowNode = event.rowNode;

      if (colId === "am_dist") {
        const am = Number(rowData.am_dist ?? 0) + Number(rowData.am_k ?? 0);
        const qt = Number(rowData.qt ?? 0);
        rowNode.setDataValue("am", am, "noEvent");
        rowNode.setDataValue("um", qt !== 0 ? am / qt : 0, "noEvent");
      }
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged: onFocusedRowChanged1,
            onCellValueChanged: onCellValueChanged1,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: { onCellValueChanged: onCellValueChanged2 },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="flex flex-col h-full w-full">
        <CommonSpinner isLoading={isLoading} />
        <div className="h-2/4 w-full">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single" }}
            events={grids.grid1.events}
          />
        </div>
        <div className="h-2/4 w-full">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single" }}
            events={grids.grid2.events}
          />
        </div>
      </div>
    );
  }
);

export default SN_TRA_IBL_REG_004_POP_01;
